import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IoniconsFilled } from '@pt/pt-models';
import { Product } from 'it/primatoo/openapi/api/sec/schemas/cybersecurity/Product';
import { Vendor } from 'it/primatoo/openapi/api/sec/schemas/cybersecurity/Vendor';
import { CyberSecuritySecService } from '@pt/api-client';

@Component({
  selector: 'pt-vendor-and-product-dialog',
  template: `
    <div class='modal-header'>
      <h4 class='modal-title'>
        {{ title | translate }}
      </h4>
    </div>

    <div class='modal-body'>
      <form [formGroup]="filterForm">
        <label *ngIf="currentVendor" class="w-100 mb-2">
          {{ 'LABELS.VENDOR' | translate }}: <b>{{ currentVendor.name }}</b>
          <small *ngIf="invalidVendorUUID"
                 class="form-text text-muted ms-2">({{ "ERROR_MESSAGE.INVALID_VENDOR_UUID" | translate }})</small>
        </label>

        <label [htmlFor]="itemFilter" class="form-label">
          {{ 'LABELS.FIND_BY_NAME'  | translate }}
        </label>

        <div class="input-group  input-group-sm mb-3">
          <input type="text"
                 [id]="itemFilter"
                 class="form-control form-control-sm"
                 placeholder=""
                 formControlName="filter">
          <span class="input-group-text" id="basic-addon1">
            <pt-icon [key]="IconFilter" [size]="12"></pt-icon>
          </span>
        </div>
      </form>
      <div class="position-relative">
        <div class="loader" *ngIf="loading">
          <pt-loader></pt-loader>
        </div>
        <div class="tableFixHead">
          <table class="table table-hover">
            <thead>
            <tr>
              <th scope="col" class="w-100 d-flex justify-content-between" style="font-weight: 100; padding: 0 10px;">
<!--                <button type='button' class='btn btn-primary btn-sm'-->
<!--                        [disabled]='page === 1'-->
<!--                        (click)='changePage(-1); $event.stopPropagation()'>-->
<!--                  <-->
<!--                </button>-->


                <ng-container *ngIf="!!currentVendor; then product; else vendor"></ng-container>

                <ng-template #vendor>
                  <span
                    *ngIf="items?.length === 20; else other">{{ 'MESSAGE.MORE_VENDOR_FOUND' | translate:{ 'value': items?.length || 0 } }}
                  </span>
                  <ng-template #other>
                    {{ 'MESSAGE.VENDOR_FOUND' | translate:{ 'value': items?.length || 0 } }}
                  </ng-template>
                </ng-template>

                <ng-template #product>
                  <span
                    *ngIf="items?.length === 20; else other">{{ 'MESSAGE.MORE_PRODUCT_FOUND' | translate:{ 'value': items?.length || 0 } }}
                  </span>
                  <ng-template #other>
                    {{ 'MESSAGE.PRODUCT_FOUND' | translate:{ 'value': items?.length || 0 } }}
                  </ng-template>
                </ng-template>

<!--                <button type='button' class='btn btn-primary btn-sm'-->
<!--                        [disabled]="!items || items?.length < 20 || items?.length === 0"-->
<!--                        (click)='changePage(1); $event.stopPropagation()'>-->
<!--                  >-->
<!--                </button>-->
              </th>
            </tr>
            </thead>
            <tbody>

            <tr class="no-data" *ngIf="items?.length === 0">
              <td>{{ 'MESSAGE.NODATA' | translate }}</td>
            </tr>

            <tr *ngFor="let v of items" (click)="activeModal.close(v)">
              <td class="table-row w-100"
                  [ngbTooltip]='v.name'
                  container='body'
                  [innerHTML]="v.name | highlight: filterForm.controls.filter.value"></td>
            </tr>

            <tr *ngIf="items?.length === 20" class="no-hover">
              <td class="table-row w-100 text-center no-hover" style="padding: 0 10px;">
                ...
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <div class='modal-footer'>
      <button #cancel type='button' class='btn btn-outline-secondary btn-sm'
              (click)='activeModal.close(); $event.stopPropagation()'>
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>
  `,
  styles: [`

    .no-hover:hover {
      cursor: default;
      background: transparent;
    }
    .no-data {
      width: 100%;
      height: 515px;
    }

    .no-data td {
      width: 100%;
      height: 515px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .no-data td:hover {
      background: white !important;
    }

    .loader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #7d7d7d7d;
      z-index: 1;
    }

    .table-row {
      padding: 2px 10px;
      height: 22px;
      cursor: pointer;
      max-height: 22px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
      width: 150px;
    }

    .tableFixHead {
      position: relative;
      overflow: auto;
      height: 570px;
      padding: 0;
    }

    .tableFixHead table {
      margin: 0;
    }

    .tableFixHead thead th {
      position: sticky;
      top: 0;
      z-index: 1;
    }

    /* Just common table stuff. Really. */
    table {
      border-collapse: collapse;
      width: 100%;
    }

    th, td {
      padding: 8px 16px;
    }

    th {
      background: #eee;
    }
  `]
})
export class VendorAndProductDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('cancel', { static: true }) cancelBtn: TemplateRef<unknown>;

  public mode: 'VENDOR' | 'PRODUCT' = 'VENDOR';
  public title?: string;
  public items: Array<Vendor | Product>;
  public loading = false;
  public itemFilter = 'vendorFilter';
  public IconFilter = IoniconsFilled.filter;

  public currentVendor?: Vendor;
  public currentProduct?: Product;

  public invalidVendorUUID = false;
  public filterForm: FormGroup = new FormGroup({
    filter: new FormControl()
  });
  public page = 1;
  private destroy$ = new Subject<boolean>();
  private requestDestroy$ = new Subject<boolean>();

  constructor(public activeModal: NgbActiveModal,
              private cyberSecuritySecService: CyberSecuritySecService) {
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.requestDestroy$.next(true);
  }


  ngAfterViewInit(): void {
    setTimeout(() => {
      const doc = document.getElementById(this.itemFilter);
      if (doc) {
        doc.focus();
      }
    }, 1000);
  }

  private init() {
    this.destroy$.next(true);
    this.filterForm.controls.filter.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe((f?: string) => {
      this.page = 1;
      this.search(f);
    });
  }

  search(filter: string): void {
    switch (this.mode) {
      case 'PRODUCT':
        this.loadProduct(this.currentVendor, filter);
        break;
      case 'VENDOR':
        this.loadVendor(filter);
        break
    }
  }

  startFindVendor(vendor?: Vendor) {
    this.mode = 'VENDOR';
    this.currentVendor = vendor;
    this.init();

    if (vendor) {
      this.filterForm.controls.filter.setValue(vendor?.name);
    } else {
      this.filterForm.controls.filter.setValue('');
    }
  }

  startFindProduct(vendor: Vendor, product?: Product) {
    this.mode = 'PRODUCT';
    this.currentVendor = vendor;
    this.currentProduct = product;
    this.init();
    if (product) {
      this.filterForm.controls.filter.setValue(product?.name);
    } else {
      this.filterForm.controls.filter.setValue('');
    }
  }


  // changePage(pageStep: number) {
  //   this.page += pageStep;
  //   const filter = this.filterForm.controls.filter.value;
  //   if (filter && filter.length > 0) {
  //     this.search(filter);
  //   } else {
  //     this.search('');
  //   }
  // }


  private loadProduct(vendor: Vendor, filter?: string) {
    this.title = 'LABELS.PRODUCT';
    this.loading = true;
    this.invalidVendorUUID = false;
    this.cyberSecuritySecService.getVendorProducts(vendor.name, filter, this.page)
      .pipe(takeUntil(this.requestDestroy$))
      .subscribe((v) => {
        this.items = v.products;
        this.loading = false;
      }, () => {
        this.loading = false;
        this.invalidVendorUUID = true;
      });
  }

  private loadVendor(filter?: string) {
    this.requestDestroy$.next(true);
    this.loading = true;
    this.cyberSecuritySecService.getVendor(filter, this.page)
      .pipe(takeUntil(this.requestDestroy$))
      .subscribe(
        (v) => {
          this.items = v.vendors;
          this.loading = false;
        }, () => {
          this.items = [];
          this.loading = false;
        });
  }
}
